//#region Imports

// @angular
import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

// rxjs
import {Observable} from 'rxjs';

// @ew
import {LoginFormValue, SignUpFormValue} from '@ew/auth-models';
import {GlobalUser, User} from '@ew/models';
import {APP_CONFIG} from '@ew/app-config';
import {API_PATHS} from '@ew/api-path';

//#endregion

declare const google: any;

@Injectable({providedIn: 'root'})
export class AuthHttpService {
  //#region Properties

  private readonly baseUrl = inject(APP_CONFIG).baseURL;
  private readonly usersUrl = `${this.baseUrl}${API_PATHS.USERS.BASE}`;
  private readonly http = inject(HttpClient);

  //#endregion

  //#region Public methods

  /**
   * Sends a POST request to the sign-up endpoint with the provided user data.
   *
   * @param {SignUpFormValue} user - The user data to be sent in the request body.
   * @return {Observable<User>} The response from the server as an observable containing User object.
   */
  public signUp(user: SignUpFormValue): Observable<User> {
    const parsedUser: Partial<SignUpFormValue> = {
      name: user.name,
      email: user.email,
      password: user.password,
    };

    return this.http.post<User>(`${this.usersUrl}/${API_PATHS.USERS.SIGN_UP}`, parsedUser);
  }

  /**
   * Sends a POST request to the login endpoint with the provided login credentials.
   *
   * @param {LoginFormValue} loginCredentials - The login credentials to be sent in the request body.
   * @returns {Observable<{ accessToken: string }>} An observable containing the response from the server.
   */
  public login(loginCredentials: LoginFormValue): Observable<{accessToken: string}> {
    return this.http.post<{accessToken: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.LOGIN}`,
      loginCredentials,
    );
  }

  /**
   * Verifies a user's account.
   *
   * @param {verificationCode} verificationCode - Verification code to be sent in the request body
   * @param {token} token - token to be sent in the request body
   * @returns {Observable<{ message: string }>} - An observable containing the response from the server.
   */
  public verifyUser(verificationCode: string, token: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.usersUrl}/${API_PATHS.USERS.VERIFY}`, {
      verificationCode,
      token,
    });
  }

  /**
   * Resend a verification code to the user's email.
   *
   * @param {token} token - The token to be decoded.
   * @returns  {Observable<{ token: string }>} - An observable containing token.
   */
  public resendVerificationCode(token: string): Observable<{token: string}> {
    return this.http.post<{token: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.RESEND_VERIFICATION_CODE}`,
      {
        token,
      },
    );
  }

  /**
   * Sends a verification code to the user's email.
   *
   * Hits the email-keyed endpoint (RESEND_VERIFICATION_CODE_EMAIL → EmailOnlyDto)
   * — the token-keyed endpoint (RESEND_VERIFICATION_CODE → TokenOnlyDto) is only
   * usable when the caller already has a (possibly expired) JWT, which the
   * /resend-code page does not.
   *
   * @param {string} email - The email address to send the verification code to.
   * @returns {Observable<{ token: string }>} An observable containing the response from the server.
   */
  public sendVerificationCode(email: string): Observable<{token: string}> {
    return this.http.post<{token: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.RESEND_VERIFICATION_CODE_EMAIL}`,
      {
        email,
      },
    );
  }

  /**
   * Sends a request to reset the password for the given email.
   *
   * @param {string} email - The email address to send the password reset request to.
   * @returns {Observable<{ message: string }>} An observable containing the response from the server.
   */
  public requestPasswordReset(email: string): Observable<{message: string}> {
    return this.http.post<{message: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.RESET_PASSWORD_REQUEST}`,
      {
        email,
      },
    );
  }

  /**
   * Resets the password for a user.
   *
   * @param {string} newPassword - The new password to be set.
   * @param {string} token - The token required to reset the password.
   * @returns {Observable<User>} An observable containing User object.
   */
  public resetPassword(newPassword: string, token: string): Observable<User> {
    return this.http.post<User>(`${this.usersUrl}/${API_PATHS.USERS.RESET_PASSWORD}/${token}`, {
      newPassword,
    });
  }

  /**
   * Refreshes the authentication token.
   *
   * @param {string} token - The token to be refreshed.
   * @returns {Observable<{accessToken: string}>} An observable containing access token.
   */
  public refreshToken(token: string): Observable<{accessToken: string}> {
    return this.http.post<{accessToken: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.REFRESH_TOKEN}`,
      {token},
    );
  }

  /**
   * Checks if the provided token is authenticated.
   *
   * @param {string} token - The token to be checked for authentication.
   * @returns {Observable<GlobalUser>} An observable containing the authentication result as a GlobalUser.
   */
  public isAuthenticated(token: string): Observable<GlobalUser> {
    return this.http.post<GlobalUser>(`${this.usersUrl}/${API_PATHS.USERS.AUTHENTICATED}`, {
      token,
    });
  }

  /**
   * Logs the user out of the application.
   *
   * @return {Observable<{ message: string }>} An observable containing the response from the server.
   */
  public logout(): Observable<{message: string}> {
    return this.http.post<{message: string}>(`${this.usersUrl}/${API_PATHS.USERS.LOGOUT}`, null);
  }

  /**
   * Logs the user in with Google.
   *
   * @description - Redirects the user to the Google login page.
   * The user will be redirected back to the application after a successful login.
   *
   * @returns {void}
   */
  public signInWithGoogle(): void {
    window.location.href = `${this.usersUrl}/${API_PATHS.USERS.GOOGLE}`;
  }

  public connectGoogle(user: User): Observable<{ message: string; user: User }> {
    return new Observable((observer) => {
      try {
        const client = google.accounts.oauth2.initCodeClient({
          client_id: '204506783784-becc4a6tuqs0id55og1cudslh9scqcs1.apps.googleusercontent.com',
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: (response: { code?: string; error?: string }) => {
            if (response.error) {
              observer.error(new Error('Google authentication cancelled'));
              return;
            }

            if (!response.code) {
              observer.error(new Error('No authorization code received'));
              return;
            }

            // Send authorization code to backend
            this.http.post<{ message: string; user: User }>(
              `${this.usersUrl}/link-google`, 
              { code: response.code, userId: user.id }
            ).subscribe({
              next: (result) => {
                observer.next(result);
                observer.complete();
              },
              error: (err) => {
                observer.error(err);
              },
            });
          },
        });

        client.requestCode(); // Opens Google popup
      } catch (error) {
        observer.error(new Error('Failed to initialize Google OAuth'));
      }
    });
  }

  /**
   * Logs the user in with Google using the mobile app.
   *
   * @param {any} googleUser - The user data from Google.
   * @returns {Observable<{accessToken: string}>} An observable containing the access token.
   */
  public signInWithGoogleMobile(googleUser: any): Observable<{accessToken: string}> {
    return this.http.post<{accessToken: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.MOBILE_GOOGLE_SIGN_IN}`,
      {
        user: googleUser,
      },
    );
  }

  public signInWithFacebookMobile(facebookUser: any): Observable<{accessToken: string}> {
    return this.http.post<{accessToken: string}>(
      `${this.usersUrl}/${API_PATHS.USERS.MOBILE_FACEBOOK_SIGN_IN}`,
      {
        user: facebookUser,
      },
    );
  }

  /**
   * Logs the user in with Facebook.
   *
   * @description - Redirects the user to the Facebook login page.
   * The user will be redirected back to the application after a successful login.
   *
   * @returns {void}
   */
  public signInWithFacebook(): void {
    window.location.href = `${this.usersUrl}/${API_PATHS.USERS.FACEBOOK}`;
  }

  /**
   * Sets up email and password for users who registered with social accounts.
   *
   * @param {Object} data - The setup data containing userId, email, and password.
   * @returns {Observable<{token: string}>} An observable containing verification token.
   */
  // Identity for these endpoints comes from the JWT (server-side) — client
  // no longer sends userId in the body. Keeps the wire contract minimal
  // and makes it impossible to accidentally spoof another user via body.
  public setupEmailAndPassword(data: { email: string; password: string }): Observable<{ token: string; message: string }> {
    return this.http.post<{ token: string; message: string }>(
      `${this.usersUrl}/setup-email`,
      data
    );
  }

  public verifyEmailSetup(data: { verificationCode: string; token: string }): Observable<{ message: string; user: any }> {
    return this.http.post<{ message: string; user: any }>(
      `${this.usersUrl}/verify-email-setup`,
      data
    );
  }

  public resendEmailVerification(data: { email: string }): Observable<{ token: string; message: string }> {
    return this.http.post<{ token: string; message: string }>(
      `${this.usersUrl}/resend-email-verification`,
      data
    );
  }

  public changePassword(data: { currentPassword: string; newPassword: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.usersUrl}/change-password`,
      data
    );
  }

  public connectFacebook(user: User): Observable<{ message: string; user: User }> {
    return new Observable((observer) => {
      console.log('connectFacebook: Starting Facebook connection process');
      
      // Check if running on localhost/HTTP and warn the user
      if (window.location.protocol === 'http:' && !window.location.hostname.includes('localhost')) {
        observer.error(new Error('Facebook login requires HTTPS. Please use "npm run web-https" for local development.'));
        return;
      }

      // Check if App ID is configured
      if ((window as any).fbAppId === 'YOUR_FACEBOOK_APP_ID' || !(window as any).fbAppId) {
        observer.error(new Error('Facebook App ID is not configured. Please add your Facebook App ID in index.html'));
        return;
      }

      // Function to initiate Facebook login
      const initiateFacebookLogin = () => {
        console.log('connectFacebook: SDK ready, initiating login');
        try {
          // Double-check FB object exists
          if (!(window as any).FB || !(window as any).FB.login) {
            throw new Error('Facebook SDK not properly initialized');
          }
          
          (window as any).FB.login((response: any) => {
            console.log('connectFacebook: Login response:', response);
            if (response.status === 'connected') {
              const accessToken = response.authResponse.accessToken;
              
              // Get user info from Facebook
              (window as any).FB.api('/me', { fields: 'id,email,name' }, (fbUser: any) => {
                console.log('connectFacebook: FB user data:', fbUser);
                // Send Facebook data to backend
                this.http.post<{ message: string; user: User }>(
                  `${this.usersUrl}/link-facebook`, 
                  { 
                    accessToken: accessToken,
                    fbId: fbUser.id,
                    email: fbUser.email,
                    name: fbUser.name,
                    userId: user.id 
                  }
                ).subscribe({
                  next: (result) => {
                    observer.next(result);
                    observer.complete();
                  },
                  error: (err) => {
                    observer.error(err);
                  },
                });
              });
            } else {
              console.log('connectFacebook: User cancelled or not authorized');
              observer.error(new Error('Facebook authentication cancelled'));
            }
          }, { scope: 'email,public_profile' });
        } catch (error) {
          console.error('connectFacebook: Error during login:', error);
          observer.error(new Error('Failed to initialize Facebook OAuth. Please check the console for details.'));
        }
      };

      // Check if FB SDK is already loaded and ready
      console.log('connectFacebook: Checking SDK status - fbSdkReady:', (window as any).fbSdkReady, 'FB:', !!(window as any).FB);
      
      if ((window as any).fbSdkReady === true && (window as any).FB) {
        initiateFacebookLogin();
      } else {
        console.log('connectFacebook: SDK not ready, waiting...');
        let attempts = 0;
        // Wait for SDK to be ready
        const checkInterval = setInterval(() => {
          attempts++;
          console.log(`connectFacebook: Checking SDK (attempt ${attempts}) - fbSdkReady:`, (window as any).fbSdkReady);
          
          if ((window as any).fbSdkReady === true && (window as any).FB) {
            clearInterval(checkInterval);
            initiateFacebookLogin();
          } else if ((window as any).fbSdkReady === false && attempts > 10) {
            // SDK explicitly failed to initialize
            clearInterval(checkInterval);
            observer.error(new Error('Facebook SDK failed to initialize. Please check your App ID configuration.'));
          }
        }, 500);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!(window as any).fbSdkReady) {
            observer.error(new Error('Facebook SDK timeout. Please check your internet connection and App ID configuration.'));
          }
        }, 10000);
      }
    });
  }

  public unlinkGoogle(): Observable<{ message: string; user: User }> {
    return this.http.post<{ message: string; user: User }>(
      `${this.usersUrl}/${API_PATHS.USERS.UNLINK_GOOGLE}`,
      {}
    );
  }

  public unlinkFacebook(): Observable<{ message: string; user: User }> {
    return this.http.post<{ message: string; user: User }>(
      `${this.usersUrl}/${API_PATHS.USERS.UNLINK_FACEBOOK}`,
      {}
    );
  }

  //#endregion
}
